import cloneDeep from "lodash/cloneDeep"
import { css } from "styled-components"

function* _range(start: number, end: number): Generator<number> {
  for (let i = start; i < end; i++) {
    yield i
  }
}

/** `0`から`end`未満の範囲 */
export function range(end: number): Array<number>
/** `start`から`end`未満の範囲 */
export function range(start: number, end: number): Array<number>
export function range(startOrEnd: number, maybeEnd?: number): Array<number> {
  const start = maybeEnd != null ? startOrEnd : 0
  const end = maybeEnd || startOrEnd
  return Array.from(_range(start, end))
}

/** `start`から`end`**以下**の範囲 */
export function rangeClosed(start: number, end: number): Array<number> {
  return Array.from(_range(start, end + 1))
}

export const fontSize = (px: number) => css`
  font-size: ${px}px;
  @media screen and (max-width: 768px) {
    font-size: ${(px * 3) / 4}px;
  }
`

export const reduceToObject = <T, Key extends string | number, Value>(
  array: Array<T>,
  fn: (value: T) => readonly [Key, Value]
) =>
  array.reduce((obj, value) => {
    const [key, a] = fn(value)
    obj[key] = a
    return obj
  }, {} as Record<Key, Value>)

/**
 * - 配列ならば要素数0の時`true`
 * - numberならば `null | undefined`の時`true`
 * - それ以外の時`!value`の時`true`
 *   - stringの場合`"" | null | undefined`がtrueになる
 */
export const isEmptyValue = (value: unknown) => {
  if (Array.isArray(value) && value.length === 0) {
    return true
  }
  if (toString.call(value).slice(8, -1).toLowerCase() === "object" && !Object.keys(value as Object).length) {
    return true
  }
  // NOTE: numberの時!valueだと0の時もtrueになってしまうため
  if (typeof value === "number" && value == null) {
    return true
  }
  if (!value) {
    return true
  }
  return false
}

/** objectの全要素を再帰的に迂回し、配列ならばvalue.length===0の時、その他は!valueの時に未入力(false)とみたす */
export const checkIsCompleted = (object: object): boolean => {
  for (const key in object) {
    const value = (object as Record<string, unknown>)[key]
    if (isEmptyValue(value)) {
      return false
    }

    if (typeof value === "object") {
      /** ネストされたobjectも再帰的にチェックし、falseだったらreturn、trueだったらチェック続行 */
      const checkDeep = checkIsCompleted(value!)
      if (!checkDeep) {
        return false
      }
    }
  }
  return true
}

/**
 * objectを再帰的に走査し、conditionにマッチしたものを削除して返却する。キーボード入力など頻繁に発生する処理には使わない！
 * @param object {Object}: Falsyなプロパティを保持しているObject
 * @param condition {Function}: isEmpty関数。空配列やオブジェクトも削除したい場合は条件に追加する
 * @example removeObjectBy({id: 0, name: 'test'}) => {name: 'test'}
 */
export const removeObjectBy = (object: Object, condition?: Function) => {
  const _obj = cloneDeep(object)
  const _cond = typeof condition === "function" ? condition : (v: any) => !Boolean(v)
  const _remove = (obj: any) => {
    for (let o in obj) {
      if (_cond(obj[o])) {
        delete obj[o] // condition に一致したプロパティを削除する
      } else if (typeof obj[o] === "object") {
        _remove(obj[o])
        if (Array.isArray(obj[o])) obj[o] = obj[o].filter(Boolean) // Arrayはdeleteで削除してもlengthは残ってしまうのでfilterする
        if (_cond(obj[o])) delete obj[o] // 空のArray|Objectはプロパティごと削除する
      }
    }
  }
  _remove(_obj)
  return _cond(_obj) ? undefined : _obj
}

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))
